import React, { useState, useEffect } from 'react';
import { Save, Upload, X, AlertCircle, CheckCircle, Camera, Link, User, Mail, Phone, CreditCard, Info, Image as ImageIcon } from 'lucide-react';
import { API_BASE_URL, IMAGE_BASE_URL } from '../../config';

const BasicInformation = ({ isSuperAdmin, onProfileUpdate, authToken: authTokenProp }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    portfolio_url: '',
    bio: '',
    artist_type: '',
    experience: '',
    bank_details: {
      account_holder: '',
      account_number: '',
      bank_name: '',
      ifsc_code: ''
    }
  });

  const [profileImage, setProfileImage] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const [currentProfileImage, setCurrentProfileImage] = useState(null);

  const [bannerImage, setBannerImage] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [currentBannerImage, setCurrentBannerImage] = useState(null);

  const [backgroundGallery, setBackgroundGallery] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const getAuthHeaders = () => {
    const token = authTokenProp ?? localStorage.getItem('authToken');
    if (!token) return {};
    return {
      'Authorization': `Bearer ${token}`,
    };
  };

  useEffect(() => {
    if (!isSuperAdmin) loadProfileInfo();
  }, [isSuperAdmin]);

  const loadProfileInfo = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/profile`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();

        let bankDetails = data.bank_details;
        if (typeof bankDetails === 'string') {
          try {
            bankDetails = JSON.parse(bankDetails);
          } catch (e) {
            console.error("Failed to parse bank details:", e);
            bankDetails = null;
          }
        }

        // Split name into first and last if needed (for data from registration)
        const fullName = data.name || '';
        const nameParts = fullName.split(' ');
        const firstName = data.first_name || nameParts[0] || '';
        const lastName = data.last_name || nameParts.slice(1).join(' ') || '';

        // Robust bank details mapping
        let bd = bankDetails || {};
        const finalBankDetails = {
          account_holder: bd.account_holder || bd.accountHolderName || bd.account_holder_name || '',
          account_number: bd.account_number || bd.accountNumber || bd.bank_account_number || '',
          bank_name: bd.bank_name || bd.bankName || '',
          ifsc_code: bd.ifsc_code || bd.ifscCode || ''
        };

        setFormData({
          first_name: firstName,
          last_name: lastName,
          email: data.email || '',
          phone: data.phone || '',
          portfolio_url: data.portfolio_url || '',
          bio: data.bio || data.description || '',
          artist_type: data.artist_type || '',
          experience: data.experience || '',
          bank_details: finalBankDetails
        });

        setBackgroundGallery(data.background_gallery || []);

        if (data.dp_image) {
          setCurrentProfileImage(data.dp_image);
        }
        if (data.banner_image) {
          setCurrentBannerImage(data.banner_image);
        }
      } else {
        setError('Failed to load profile information');
      }
    } catch (error) {
      setError('Failed to load profile information');
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent] || {}), // Critical: Handles 'null' bank_details safely
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    if (error) setError('');
  };

  const handleImageChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError(`${type === 'profile' ? 'Profile' : 'Banner'} image must be less than 2MB`);
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      if (type === 'profile') {
        if (profilePreview) URL.revokeObjectURL(profilePreview);
        setProfileImage(file);
        setProfilePreview(previewUrl);
      } else {
        if (bannerPreview) URL.revokeObjectURL(bannerPreview);
        setBannerImage(file);
        setBannerPreview(previewUrl);
      }
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSaveLoading(true);
    setError('');
    setSuccess('');

    try {
      const submitData = new FormData();

      submitData.append('first_name', formData.first_name);
      submitData.append('last_name', formData.last_name);
      submitData.append('name', `${formData.first_name} ${formData.last_name}`.trim()); // Critical: Required for Art Network cards
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('portfolio_url', formData.portfolio_url);
      submitData.append('bio', formData.bio);
      submitData.append('description', formData.bio); // Critical: Required for back-end description fields
      submitData.append('artist_type', formData.artist_type);
      submitData.append('experience', formData.experience);
      submitData.append('bank_details', JSON.stringify(formData.bank_details));

      // Send individual bank fields for reliable back-end parsing
      if (formData.bank_details) {
        submitData.append('account_holder', formData.bank_details.account_holder || '');
        submitData.append('account_number', formData.bank_details.account_number || '');
        submitData.append('bank_name', formData.bank_details.bank_name || '');
        submitData.append('ifsc_code', formData.bank_details.ifsc_code || '');
      }

      submitData.append('background_gallery', JSON.stringify(backgroundGallery));

      if (profileImage) {
        submitData.append('dp_image', profileImage);
      }
      if (bannerImage) {
        submitData.append('banner_image', bannerImage);
      }

      const token = authTokenProp ?? localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/admin/profile`, {
        method: 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: submitData
      });

      if (response.ok) {
        setSuccess('Profile updated successfully!');
        const result = await response.json();
        const data = result.data || result;
        if (data.dp_image) setCurrentProfileImage(data.dp_image);
        if (data.banner_image) setCurrentBannerImage(data.banner_image);

        setProfileImage(null);
        setBannerImage(null);

        // Refetch profile so header (name, dp) always shows latest from server
        if (onProfileUpdate) onProfileUpdate();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to update profile');
      }
    } catch (error) {
      setError('Network error occurred');
    }
    setSaveLoading(false);
  };

  const getImageUrl = (preview, current) => {
    if (preview) return preview;
    if (current) {
      if (current.startsWith('http')) return current;
      const path = current.replace(/^\//, '');
      return `${IMAGE_BASE_URL}${path}`;
    }
    return null;
  };

  if (isSuperAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Profile Settings</h1>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800">
          <p>Login as a provider to manage your profile.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto pb-20">
      <div className="fixed top-20 right-6 z-50 space-y-2 w-80">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-4 shadow-lg flex items-start animate-in slide-in-from-right">
            <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-700 font-medium">{error}</p>
            </div>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        {success && (
          <div className="bg-green-50 border-l-4 border-green-500 rounded-r-lg p-4 shadow-lg flex items-start animate-in slide-in-from-right">
            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-green-700 font-medium">{success}</p>
            </div>
            <button onClick={() => setSuccess('')} className="text-green-400 hover:text-green-600">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        <div className="relative h-64 bg-gradient-to-r from-indigo-500 to-purple-600 group">
          {getImageUrl(bannerPreview, currentBannerImage) ? (
            <img
              src={getImageUrl(bannerPreview, currentBannerImage)}
              alt="Banner"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-white/70">
              <ImageIcon className="h-12 w-12 mb-2" />
              <p className="font-medium">Add a banner image</p>
            </div>
          )}
          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <label className="bg-white/90 hover:bg-white text-gray-900 px-4 py-2 rounded-full cursor-pointer flex items-center space-x-2 transition-all shadow-lg transform hover:scale-105 font-medium">
              <Camera className="h-4 w-4" />
              <span>Change Banner</span>
              <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageChange(e, 'banner')} />
            </label>
          </div>
        </div>

        <div className="relative px-8 pt-0 pb-8 mt-[-64px]">
          <div className="flex flex-col md:flex-row md:items-end md:space-x-6">
            <div className="relative group">
              <div className="h-32 w-32 rounded-full p-1 bg-white shadow-xl ring-4 ring-white relative overflow-hidden">
                {getImageUrl(profilePreview, currentProfileImage) ? (
                  <img
                    src={getImageUrl(profilePreview, currentProfileImage)}
                    alt="Profile"
                    className="w-full h-full object-cover rounded-full"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 rounded-full">
                    <User className="h-12 w-12" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                  <Camera className="h-6 w-6 text-white" />
                </div>
                <input
                  type="file"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  accept="image/*"
                  onChange={(e) => handleImageChange(e, 'profile')}
                />
              </div>
            </div>

            <div className="mt-6 md:mt-0 flex-1 flex flex-col md:flex-row md:items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">
                  {formData.first_name || 'Your'} {formData.last_name || 'Name'}
                </h2>
                <p className="text-gray-500 font-medium">Provider Profile</p>
              </div>
              <button
                onClick={handleSubmit}
                disabled={saveLoading}
                className="mt-4 md:mt-0 flex items-center justify-center space-x-2 bg-indigo-600 text-white px-8 py-3 rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-all shadow-lg shadow-indigo-200 font-semibold"
              >
                {saveLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <Save className="h-5 w-5" />
                )}
                <span>{saveLoading ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <div className="bg-gray-50/50 rounded-2xl p-6 border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                  <span className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center mr-3">
                    <User className="h-4 w-4" />
                  </span>
                  Personal Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">First Name</label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">Last Name</label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                      placeholder="Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full bg-white border border-gray-200 rounded-xl pl-11 pr-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">Phone Number</label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full bg-white border border-gray-200 rounded-xl pl-11 pr-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                        placeholder="+91 9988776655"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">Portfolio URL</label>
                    <div className="relative">
                      <Link className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="url"
                        name="portfolio_url"
                        value={formData.portfolio_url}
                        onChange={handleInputChange}
                        className="w-full bg-white border border-gray-200 rounded-xl pl-11 pr-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                        placeholder="https://yourportfolio.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">Artist Type</label>
                    <input
                      type="text"
                      name="artist_type"
                      value={formData.artist_type}
                      onChange={handleInputChange}
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                      placeholder="e.g. Tattoo Artist"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">Experience</label>
                    <input
                      type="text"
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                      placeholder="e.g. 5 Years"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">Bio</label>
                    <textarea
                      name="bio"
                      value={formData.bio}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none resize-none"
                      placeholder="Tell us about yourself..."
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50/50 rounded-2xl p-6 border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                  <span className="w-8 h-8 rounded-lg bg-green-100 text-green-600 flex items-center justify-center mr-3">
                    <CreditCard className="h-4 w-4" />
                  </span>
                  Bank Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">Account Holder</label>
                    <input
                      type="text"
                      name="bank_details.account_holder"
                      value={formData.bank_details.account_holder}
                      onChange={handleInputChange}
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">Account Number</label>
                    <input
                      type="text"
                      name="bank_details.account_number"
                      value={formData.bank_details.account_number}
                      onChange={handleInputChange}
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                      placeholder="000000000000"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">Bank Name</label>
                    <input
                      type="text"
                      name="bank_details.bank_name"
                      value={formData.bank_details.bank_name}
                      onChange={handleInputChange}
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                      placeholder="HDFC Bank"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">IFSC Code</label>
                    <input
                      type="text"
                      name="bank_details.ifsc_code"
                      value={formData.bank_details.ifsc_code}
                      onChange={handleInputChange}
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none uppercase"
                      placeholder="HDFC0001234"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50/50 rounded-2xl p-6 border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                  <span className="w-8 h-8 rounded-lg bg-pink-100 text-pink-600 flex items-center justify-center mr-3">
                    <ImageIcon className="h-4 w-4" />
                  </span>
                  Background Gallery
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-6">
                  {backgroundGallery.map((img, index) => (
                    <div key={index} className="relative group aspect-square rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                      <img
                        src={img.startsWith('http') ? img : `${API_BASE_URL}${img}`}
                        alt={`Gallery ${index}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => setBackgroundGallery(prev => prev.filter((_, i) => i !== index))}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <label className="aspect-square rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors group">
                    <Upload className="h-6 w-6 text-gray-400 group-hover:text-indigo-500 mb-2 transition-colors" />
                    <span className="text-xs font-medium text-gray-500 group-hover:text-indigo-600 transition-colors">Upload</span>
                    <input
                      type="file"
                      className="hidden"
                      multiple
                      accept="image/*"
                      onChange={async (e) => {
                        const files = Array.from(e.target.files);
                        if (files.length > 0) {
                          setSuccess(`${files.length} images selected.`);
                        }
                      }}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-indigo-600 rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
                <div className="relative z-10">
                  <div className="flex items-center space-x-2 mb-4">
                    <Info className="h-5 w-5 text-indigo-200" />
                    <h4 className="font-bold">Profile Tips</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-indigo-100">
                    <li className="flex items-start"><span className="mr-2">•</span>Use a high-quality DP.</li>
                    <li className="flex items-start"><span className="mr-2">•</span>Complete your bio.</li>
                    <li className="flex items-start"><span className="mr-2">•</span>Verify bank details.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicInformation;
